import { FC, SVGProps, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Browser } from '@capacitor/browser';
import { Stack } from '@mui/material';
import * as Sentry from '@sentry/capacitor';

import { BusinessLayerContext, useUser } from '@mopla/business-logic';
import { useI18nLanguage } from '@mopla/utils';

import { Loader } from '../../atoms/loader/Loader';
import { MplButton } from '../../atoms/MplButton';
import { useMplSnackbar } from '../../hooks/useMplSnackbar';
import { MplErrorModal } from '../../organisms/MplModal';
import { MplSnackbar } from '../MplSnackbar/MplSnackbar';

import { GoogleWalletStyledButton } from './GoogleWalletButton.styles';

interface IProps {
	ticketId: string;
}

interface IGoogleWalletResponse {
	link: string;
}

interface ILazySvg {
	loading: boolean;
	error: Error | undefined;
	SVG: FC<SVGProps<SVGSVGElement>> | undefined;
}

const useLazySvg = (language = 'en'): ILazySvg => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<Error>();
	const Svg = useRef<FC<SVGProps<SVGSVGElement>>>();

	useEffect(() => {
		setLoading(true);
		(async () => {
			try {
				switch (language) {
					case 'cs':
						Svg.current = (
							await import(
								`../../icons/GoogleWallet/cz_add_to_google_wallet_add-wallet-badge.svg`
							)
						).default;
						break;
					case 'de':
						Svg.current = (
							await import(
								`../../icons/GoogleWallet/de_add_to_google_wallet_add-wallet-badge.svg`
							)
						).default;
						break;
					case 'es':
						Svg.current = (
							await import(
								`../../icons/GoogleWallet/esES_add_to_google_wallet_add-wallet-badge.svg`
							)
						).default;
						break;
					case 'fr':
						Svg.current = (
							await import(
								`../../icons/GoogleWallet/frFR_add_to_google_wallet_add-wallet-badge.svg`
							)
						).default;
						break;
					case 'it':
						Svg.current = (
							await import(
								`../../icons/GoogleWallet/it_add_to_google_wallet_add-wallet-badge.svg`
							)
						).default;
						break;
					case 'pl_PL':
						Svg.current = (
							await import(
								`../../icons/GoogleWallet/pl_add_to_google_wallet_add-wallet-badge.svg`
							)
						).default;
						break;
					case 'tr':
						Svg.current = (
							await import(
								`../../icons/GoogleWallet/tr_add_to_google_wallet_add-wallet-badge.svg`
							)
						).default;
						break;
					case 'uk_UA':
						Svg.current = (
							await import(
								`../../icons/GoogleWallet/uk_add_to_google_wallet_add-wallet-badge.svg`
							)
						).default;
						break;
					case 'en':
					default:
						Svg.current = (
							await import(
								`../../icons/GoogleWallet/enGB_add_to_google_wallet_add-wallet-badge.svg`
							)
						).default;
				}
			} catch (e) {
				setError(e as Error);
			} finally {
				setLoading(false);
			}
		})();
	}, [language]);

	return {
		loading,
		error,
		SVG: Svg.current,
	};
};

export const GoogleWalletButton: FC<IProps> = ({ ticketId }) => {
	const { t } = useTranslation();

	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<boolean>(false);
	const businessLayer = useContext(BusinessLayerContext);
	const language = useI18nLanguage();
	const { userData } = useUser();
	const { showSnackbar, snackbar, autoHideDuration, hideSnackbar } =
		useMplSnackbar();
	const addToWalletDisabled = !userData?.canAddTicketToWallet;

	const isProcessingRef = useRef(false);

	const handleClick = async () => {
		if (isProcessingRef.current) return;
		isProcessingRef.current = true;

		try {
			if (addToWalletDisabled) {
				showSnackbar(t('dticket:text.add_to_wallet_unavailable'));
				return;
			}

			setLoading(true);
			setError(false);

			const response = await businessLayer.api.get<IGoogleWalletResponse>(
				`/api/passengers/googleWallet?ticketId=${ticketId}`
			);

			await Browser.open({ url: response.link });
		} catch (e) {
			Sentry.captureException(e);
			setError(true);
		} finally {
			setLoading(false);
			isProcessingRef.current = false;
		}
	};

	const {
		SVG: GoogleWallet,
		loading: SVGLoading,
		error: SVGError,
	} = useLazySvg(language);
	if (loading || SVGLoading) {
		return <Loader />;
	}

	if (!userData) {
		return null;
	}

	return (
		<>
			{GoogleWallet && (
				<Stack width="100%" alignItems="center">
					<GoogleWalletStyledButton
						aria-disabled={addToWalletDisabled}
						aria-label={t('dticket:text.add_to_google_wallet')}
						onClick={handleClick}
						disabled={loading || snackbar.open}
						data-testid="google-wallet-button"
					>
						<GoogleWallet data-testid="google-wallet-icon" />
					</GoogleWalletStyledButton>
				</Stack>
			)}
			<MplSnackbar
				open={snackbar.open}
				message={snackbar.message}
				onClose={hideSnackbar}
				autoHideDuration={autoHideDuration}
				dataTestId="add-to-wallet-unavailable-snackbar"
			/>
			<MplErrorModal
				open={Boolean(error || SVGError)}
				testId="GoogleWalletButton-MplErrorModal"
				buttons={
					<MplButton fullWidth onClick={() => setError(false)}>
						{t('errors:button.close')}
					</MplButton>
				}
			/>
		</>
	);
};
